// @ts-nocheck
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { Card, Code, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import {
  AlertCard,
  AlertList,
  InsightCard,
} from '../../../../components/advanced/Widgets';
import { mockAlerts, mockCriticalAlerts, mockRecentAlerts } from '../../mockData/widgets';

export default function AlertWidgetsPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Alert / Insight Widgets</Title>
        <Text c="dimmed" size="sm">
          Display system alerts, warnings, and actionable insights with severity-based styling
          and dismiss functionality.
        </Text>
      </Stack>

      {/* Alert Card */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>AlertCard</Title>
          <Text size="sm" c="dimmed">
            Single alert display with severity indicators and optional actions.
          </Text>

          <Stack gap="sm">
            {mockAlerts.slice(0, 4).map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                showTimestamp
                onDismiss={(id) => console.log('Dismissed:', id)}
              />
            ))}
          </Stack>

          <Code block>
{`<AlertCard
  alert={{
    id: '1',
    title: 'Database Connection Failed',
    message: 'Unable to connect to primary database...',
    severity: 'error',
    timestamp: new Date()
  }}
  onDismiss={(id) => handleDismiss(id)}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Alert List */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>AlertList</Title>
          <Text size="sm" c="dimmed">
            Displays multiple alerts with filtering, sorting, and "view all" functionality.
          </Text>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <AlertList
              alerts={mockRecentAlerts}
              maxItems={5}
              onDismiss={(id) => console.log('Dismissed:', id)}
              onViewAll={() => console.log('View all')}
            />

            <AlertList
              alerts={mockCriticalAlerts}
              maxItems={5}
              filterBySeverity={['error']}
              variant="compact"
              onDismiss={(id) => console.log('Dismissed:', id)}
            />
          </SimpleGrid>

          <Code block>
{`<AlertList
  alerts={allAlerts}
  maxItems={5}
  filterBySeverity={['error', 'warning']}
  onDismiss={handleDismiss}
  onViewAll={handleViewAll}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Insight Card */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>InsightCard</Title>
          <Text size="sm" c="dimmed">
            Highlighted insight or recommendation with action buttons.
          </Text>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <InsightCard
              title="Performance Optimization Recommended"
              message="Your database queries could be 40% faster with proper indexing. Click to view recommended indexes."
              severity="warning"
              actions={[
                {
                  label: 'View Recommendations',
                  onClick: () => console.log('View recommendations'),
                  variant: 'primary',
                  icon: <IconCheck size={16} />,
                },
                {
                  label: 'Learn More',
                  onClick: () => console.log('Learn more'),
                  variant: 'secondary',
                },
              ]}
            />

            <InsightCard
              title="Critical Security Update"
              message="A security patch is available for your system. Update recommended within 24 hours."
              severity="error"
              icon={<IconAlertCircle size={24} />}
              variant="gradient"
              gradientVariant="danger"
              actions={[
                {
                  label: 'Update Now',
                  onClick: () => console.log('Update'),
                  variant: 'primary',
                },
              ]}
            />
          </SimpleGrid>

          <Code block>
{`<InsightCard
  title="Performance Optimization"
  message="Your queries could be 40% faster..."
  severity="warning"
  actions={[
    { label: 'View Details', onClick: handleClick }
  ]}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>System Monitoring</Text>
              <Text size="xs" c="dimmed">
                Display real-time system alerts and errors for DevOps teams.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>User Notifications</Text>
              <Text size="xs" c="dimmed">
                Show important messages and updates to users.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Actionable Insights</Text>
              <Text size="xs" c="dimmed">
                Highlight optimization opportunities and recommendations.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Security Alerts</Text>
              <Text size="xs" c="dimmed">
                Critical security notifications requiring immediate attention.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
